/**
 * Vote du mode collaboratif : un tableau apres l'autre, une voix par tableau.
 *
 * LE TABLEAU FINI EST L'ETAT PAR DEFAUT. On voit d'abord le resultat, entier,
 * et l'on demande le detail si on le veut : « Jouer » rejoue ce qu'UN auteur a
 * ajoute, autant de fois qu'on le souhaite. Rien ne bouge sans qu'on l'ait
 * demande. Un defile automatique imposait son rythme a tout le monde, obligeait
 * a attendre avant de pouvoir juger, et empechait de revoir une retouche qu'on
 * avait ratee.
 *
 * DEUX BOUTONS PAR AUTEUR, ecrits en toutes lettres. « Jouer » montre,
 * « Voter » choisit : une icone ou une couleur laisserait deviner, et c'est
 * exactement le moment ou il ne faut pas se tromper de geste.
 *
 * Le rejeu tourne dans une boucle rAF hors React : un setState par image ferait
 * exactement ce que le CLAUDE.md interdit.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { collabSlot, type CollabBoard as Board, type RoomSnapshot } from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { playerById } from '../store/roomStore.js';
import { Avatar } from '../ui/Avatar.js';
import { PhaseTimer } from '../ui/PhaseTimer.js';
import { PaintingIcon } from '../ui/icons.js';
import { prepareArtwork, type PreparedArtwork } from '../gallery/renderArtwork.js';

/**
 * Duree du rejeu d'UNE retouche, en millisecondes.
 *
 * Court : on rejoue un geste, pas une seance. C'est une relecture a la demande,
 * qu'on redeclenche d'un clic si on veut la revoir.
 */
const REPLAY_MS = 900;

export interface CollabBoardProps {
  readonly room: RoomSnapshot;
  readonly boards: readonly Board[];
  /** Retouches, indexees par le couple (tableau, auteur). */
  readonly drawings: Record<number, ArrayBuffer>;
  readonly selfId: string | null;
  /** Mes voix : tableau vers ordre de l'auteur choisi. */
  readonly mine: Record<number, number>;
  /** Depouillement, null tant que le vote n'est pas clos. */
  readonly tally: readonly (readonly number[])[] | null;
  readonly onVote: (board: number, author: number) => void;
  /** Fin de la phase, horodatage serveur. Absent a la revelation. */
  readonly endsAt?: number | null;
  /** Duree totale de la phase, pour l'anneau du chrono. */
  readonly durationMs?: number;
}

export function CollabBoard({
  room,
  boards,
  drawings,
  selfId,
  mine,
  tally,
  onVote,
  endsAt = null,
  durationMs = 0,
}: CollabBoardProps): ReactElement {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const preparedRef = useRef<PreparedArtwork | null>(null);
  const rafRef = useRef(0);

  const [index, setIndex] = useState(0);
  const board = boards[index];
  /** Auteur dont la retouche est en train d'etre rejouee, ou null. */
  const [playing, setPlaying] = useState<number | null>(null);
  const revealed = tally !== null;

  /** Les retouches de CE tableau, dans l'ordre, avec la palette de chacune. */
  const layers = useMemo(() => {
    if (board === undefined) return [];
    return board.authors.map((_, order) => ({
      drawing: drawings[collabSlot(board.slot, order)] ?? null,
      palette: board.palettes[order] ?? [],
    }));
  }, [board, drawings]);

  /** Repeint le tableau FINI : c'est l'etat au repos. */
  const showFinished = useCallback(() => {
    const prepared = preparedRef.current;
    const canvas = canvasRef.current;
    if (prepared === null || canvas === null) return;
    prepared.setLayerProgress(Math.max(layers.length - 1, 0), 1);
    prepared.paint(canvas, { size: canvas.clientWidth || 480, badge: true });
  }, [layers.length]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || board === undefined) return;

    let alive = true;
    preparedRef.current = null;
    setPlaying(null);

    void prepareArtwork({
      file: board.file,
      zone: board.zone,
      palette: board.palettes[0] ?? [],
      drawing: null,
      layers,
    })
      .then((prepared) => {
        if (!alive) return;
        preparedRef.current = prepared;
        prepared.setLayerProgress(Math.max(layers.length - 1, 0), 1);
        prepared.paint(canvas, { size: canvas.clientWidth || 480, badge: true });
      })
      .catch(() => undefined);

    return () => {
      alive = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [board, layers]);

  /**
   * Rejoue ce qu'un auteur a ajoute, sur ce que les precedents avaient laisse.
   *
   * On voit donc son APPORT et pas un dessin isole : c'est exactement ce qu'on
   * lui demande de juger. Relancable a volonte, et un nouveau clic remplace le
   * rejeu en cours plutot que d'en empiler un second.
   */
  const play = useCallback(
    (order: number) => {
      const prepared = preparedRef.current;
      const canvas = canvasRef.current;
      if (prepared === null || canvas === null) return;

      cancelAnimationFrame(rafRef.current);
      setPlaying(order);
      const startedAt = performance.now();

      const frame = (): void => {
        const progress = Math.min((performance.now() - startedAt) / REPLAY_MS, 1);
        prepared.setLayerProgress(order, progress);
        prepared.paint(canvas, { size: canvas.clientWidth || 480, badge: true });
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(frame);
          return;
        }
        // Retour au tableau fini : l'etat au repos est toujours le meme, quelle
        // que soit la derniere retouche regardee.
        setPlaying(null);
        showFinished();
      };
      rafRef.current = requestAnimationFrame(frame);
    },
    [showFinished],
  );

  const move = useCallback(
    (delta: number) => {
      setIndex((current) => Math.min(Math.max(current + delta, 0), boards.length - 1));
    },
    [boards.length],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target !== null && /^(INPUT|TEXTAREA)$/.test(target.tagName)) return;
      if (event.key === 'ArrowLeft') move(-1);
      if (event.key === 'ArrowRight') move(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [move]);

  if (board === undefined) return <div className="collab" />;

  const myVote = mine[board.slot];

  return (
    <div className="collab">
      <div className="collab__stage">
        <canvas ref={canvasRef} className="collab__canvas" />

        <button
          type="button"
          className="collab__nav collab__nav--prev"
          onClick={() => move(-1)}
          disabled={index === 0}
          aria-label={t('contest.previous')}
        >
          ‹
        </button>
        <button
          type="button"
          className="collab__nav collab__nav--next"
          onClick={() => move(1)}
          disabled={index >= boards.length - 1}
          aria-label={t('contest.next')}
        >
          ›
        </button>
      </div>

      {/* La consigne dit les deux gestes, et lequel sert a quoi. Sans elle, on
          clique au hasard sur le premier bouton venu, et un vote ne se reprend
          pas. */}
      {/* Le chrono manquait completement : on votait sans savoir combien de
          temps il restait, et la phase se fermait sans prevenir. */}
      {endsAt !== null && (
        <div className="collab__timer">
          <PhaseTimer endsAt={endsAt} durationMs={durationMs} />
        </div>
      )}

      <p className="collab__prompt">{revealed ? t('collab.results') : t('collab.howTo')}</p>

      <ul className="collab__authors">
        {board.authors.map((authorId, order) => {
          const player = playerById(room, authorId);
          const isMine = authorId === selfId;
          const votes = tally?.[board.slot]?.[order] ?? 0;
          const chosen = myVote === order;
          return (
            <li className="collab__row" key={`${board.slot}-${order}`} data-chosen={chosen}>
              <span className="collab__who">
                {player !== null && (
                  <Avatar
                    playerId={player.id}
                    avatar={player.avatar}
                    colorIndex={player.colorIndex}
                    size={24}
                  />
                )}
                {player?.nickname ?? '?'}
                {isMine && <span className="collab__you">{t('contest.yours')}</span>}
              </span>

              {revealed ? (
                <strong className="collab__score mono-tabular">
                  {t('collab.votes', { n: votes })}
                </strong>
              ) : (
                <span className="collab__actions">
                  <button
                    type="button"
                    className="button-ghost collab__play"
                    onClick={() => play(order)}
                    aria-busy={playing === order}
                  >
                    {t('collab.play')}
                  </button>
                  <button
                    type="button"
                    className={chosen ? 'button-primary collab__vote' : 'button-ghost collab__vote'}
                    // On ne vote pas pour soi : c'est la raison meme pour
                    // laquelle ce mode demande trois joueurs.
                    disabled={isMine}
                    onClick={() => onVote(board.slot, order)}
                  >
                    {chosen ? t('collab.voted') : t('collab.vote')}
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ul>

      <div className="collab__strip" role="tablist" aria-label={t('collab.boards')}>
        {boards.map((entry, position) => (
          <button
            key={entry.slot}
            type="button"
            role="tab"
            aria-selected={position === index}
            className="collab__dot"
            data-active={position === index}
            data-voted={mine[entry.slot] !== undefined}
            aria-label={`${position + 1}`}
            onClick={() => setIndex(position)}
          >
            {position + 1}
            {/* Un petit tableau sous le chiffre : sans lui, une rangee de
                chiffres ne dit pas ce qu'elle selectionne. */}
            <PaintingIcon size={14} />
          </button>
        ))}
      </div>
    </div>
  );
}
